// Electrical Services Section Component
import {
    MdElectricalServices,
    MdLocalFireDepartment,
    MdOutlineDescription,
    MdPhone,
    MdEmail,
} from 'react-icons/md';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

const ElectricalServicesSection = forwardRef(({ onDataUpdate, initialData }, ref) => {
    // Initialize state with initialData if it exists
    const [electricalNeeds, setElectricalNeeds] = useState(initialData?.selectedServices || []);
    const [propertyType, setPropertyType] = useState(initialData?.propertyType || 'residential');
    const [propertySize, setPropertySize] = useState(initialData?.propertySize || 'medium');
    const [urgency, setUrgency] = useState(initialData?.urgency || 'standard');
    const [additionalDetails, setAdditionalDetails] = useState(initialData?.additionalDetails || '');

    // Validation error for no service selected
    const [noServiceError, setNoServiceError] = useState(false);

    // Expose validate() to parent via ref
    useImperativeHandle(ref, () => ({
        validate() {
            if (electricalNeeds.length === 0) {
                setNoServiceError(true);
                return false;
            }
            setNoServiceError(false);
            return true;
        }
    }));

    const electricalServices = [
        { id: 'rewiring', label: 'Complete Rewiring', desc: 'Full electrical system replacement' },
        { id: 'panel-upgrade', label: 'Panel Upgrade', desc: 'Main electrical panel replacement' },
        { id: 'lighting', label: 'LED Lighting', desc: 'Energy-efficient lighting installation' },
        { id: 'outlets', label: 'New Outlets', desc: 'Additional power points installation' },
        { id: 'gate-installation', label: 'Electrical Gate Installation', desc: 'Automated gate motor and control systems' },
        { id: 'surge', label: 'Surge Protection', desc: 'Whole-house surge protection' },
        { id: 'inspection', label: 'Safety Inspection', desc: 'Complete electrical inspection' },
    ];

    const propertyTypes = [
        { id: 'residential', label: 'Residential' },
        { id: 'commercial', label: 'Commercial' },
        { id: 'industrial', label: 'Industrial' },
        { id: 'agricultural', label: 'Agricultural' },
    ];

    const propertySizes = [
        { id: 'small', label: 'Small (<100m²)' },
        { id: 'medium', label: 'Medium (100-300m²)' },
        { id: 'large', label: 'Large (300-600m²)' },
        { id: 'xlarge', label: 'Extra Large (>600m²)' },
    ];

    const urgencyOptions = [
        { id: 'emergency', label: 'Emergency' },
        { id: 'urgent', label: 'Urgent (1 week)' },
        { id: 'standard', label: 'Standard (2-4 weeks)' },
        { id: 'flexible', label: 'Flexible (>1 month)' },
    ];

    const toggleService = (serviceId) => {
        setElectricalNeeds(prev => {
            const updated = prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId];
            if (updated.length > 0) setNoServiceError(false);
            return updated;
        });
    };

    useEffect(() => {
        if (onDataUpdate) {
            onDataUpdate({
                selectedServices: electricalNeeds,
                propertyType,
                propertySize,
                urgency,
                additionalDetails,
            });
        }
    }, [electricalNeeds, propertyType, propertySize, urgency, additionalDetails, onDataUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="bg-white rounded-xl shadow-lg p-8" data-aos="fade-up">
            <div className="flex items-center mb-8">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                    <MdElectricalServices className="text-3xl text-blue-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Electrical Services Consultation</h2>
                    <p className="text-gray-600">Select your required electrical services and provide details</p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column - Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Property Type</label>
                        <div className="grid grid-cols-2 gap-4">
                            {propertyTypes.map(type => (
                                <label key={type.id} className="relative">
                                    <input
                                        type="radio"
                                        name="propertyType"
                                        value={type.id}
                                        checked={propertyType === type.id}
                                        onChange={(e) => setPropertyType(e.target.value)}
                                        className="sr-only peer"
                                    />
                                    <div className={`p-4 border-2 rounded-lg text-center cursor-pointer transition-all ${propertyType === type.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 hover:border-blue-300'
                                        }`}>
                                        <div className="font-medium text-gray-800">{type.label}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Property Size</label>
                        <div className="space-y-3">
                            {propertySizes.map(size => (
                                <label key={size.id} className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-300">
                                    <input
                                        type="radio"
                                        name="propertySize"
                                        value={size.id}
                                        checked={propertySize === size.id}
                                        onChange={(e) => setPropertySize(e.target.value)}
                                        className="h-5 w-5 text-blue-600 mr-4"
                                    />
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-800">{size.label}</div>
                                        
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-4">Service Urgency</label>
                        <div className="space-y-3">
                            {urgencyOptions.map(option => (
                                <label key={option.id} className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-300">
                                    <input
                                        type="radio"
                                        name="urgency"
                                        value={option.id}
                                        checked={urgency === option.id}
                                        onChange={(e) => setUrgency(e.target.value)}
                                        className="h-5 w-5 text-blue-600 mr-4"
                                    />
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-800">{option.label}</div>
                                    </div>
                                    {option.id === 'emergency' && (
                                        <MdLocalFireDepartment className="text-xl text-red-500" />
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-4">
                            <div className="flex items-center mb-2">
                                <MdOutlineDescription className="mr-2" />
                                Additional Details
                            </div>
                        </label>
                        <textarea
                            value={additionalDetails}
                            onChange={(e) => setAdditionalDetails(e.target.value)}
                            className="w-full h-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Describe any specific electrical issues, desired outcomes, or special requirements..."
                        />
                    </div>
                </div>

                {/* Right Column - Services & Results */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-4">
                            Select Electrical Services
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        {noServiceError && (
                            <div className="mb-3 px-4 py-2 bg-red-50 border border-red-300 rounded-lg">
                                <p className="text-sm text-red-600 font-medium">⚠ Please select at least one electrical service to continue.</p>
                            </div>
                        )}
                        <div className={`space-y-3 ${noServiceError ? 'ring-2 ring-red-400 rounded-lg p-2' : ''}`}>
                            {electricalServices.map(service => (
                                <label key={service.id} className="block">
                                    <div className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${electricalNeeds.includes(service.id)
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 hover:border-blue-300'
                                        }`}>
                                        <input
                                            type="checkbox"
                                            checked={electricalNeeds.includes(service.id)}
                                            onChange={() => toggleService(service.id)}
                                            className="h-5 w-5 text-blue-600 mr-4"
                                        />
                                        <div className="flex-1">
                                            <div className="font-medium text-gray-800">{service.label}</div>
                                            <div className="text-sm text-gray-600">{service.desc}</div>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* Contact Info Banner */}
            <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm shrink-0">
                    <MdElectricalServices className="text-xl" />
                    Need help? Contact us directly:
                </div>
                <div className="flex flex-col sm:flex-row gap-3 text-sm">
                    <a
                        href="tel:+26825185414"
                        className="flex items-center gap-2 text-blue-800 font-medium hover:text-blue-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 5414
                    </a>
                    <a
                        href="tel:+26825187424"
                        className="flex items-center gap-2 text-blue-800 font-medium hover:text-blue-600 transition-colors"
                    >
                        <MdPhone className="text-base shrink-0" />
                        +268 2518 7424
                    </a>
                    <a
                        href="mailto:info@alternative.co.sz"
                        className="flex items-center gap-2 text-blue-800 font-medium hover:text-blue-600 transition-colors"
                    >
                        <MdEmail className="text-base shrink-0" />
                        info@alternative.co.sz
                    </a>
                </div>
            </div>
        </div>
    );
});

ElectricalServicesSection.displayName = 'ElectricalServicesSection';

export default ElectricalServicesSection;